import {makeAutoObservable} from "mobx";

// Базовый лимит времени игры.
export const DEFAULT_GAME_TIME_SECONDS = 60;
export const GRID_SIZE = 7;

const buildEmptyGrid = () => Array.from({length: GRID_SIZE}, () => Array.from({length: GRID_SIZE}, () => ""));

// Преобразует входные данные поля к матрице 7x7.
const normalizeGrid = (value) => {
    if (!Array.isArray(value) || value.length !== GRID_SIZE) {
        return buildEmptyGrid();
    }

    return value.map((row) => {
        if (!Array.isArray(row) || row.length !== GRID_SIZE) {
            return Array.from({length: GRID_SIZE}, () => "");
        }

        return row.map((cell) => String(cell ?? "").slice(0, 1).toLowerCase());
    });
};

class Game {
    // Флаг активной игровой сессии.
    isActive = true;

    // Идентификатор игры на сервере.
    gameID = "";

    // Текущий счёт и рекорд.
    score = 0;
    bestScore = 0;

    // Комбо и время последнего подтверждённого слова.
    comboCount = 0;
    lastComboTimestamp = null;

    // Таймер.
    timeLeft = DEFAULT_GAME_TIME_SECONDS;
    timeLimitSeconds = DEFAULT_GAME_TIME_SECONDS;

    // Поле и выбор пользователя.
    gridSize = GRID_SIZE;
    grid = buildEmptyGrid();
    selectionCells = [];

    // Цели режима поиска слов.
    targetWordLengths = [];
    targetWordPaths = [];
    foundWords = [];
    foundWordPaths = [];

    // Подсказки и балансы.
    hintWallet = {};
    hintLimits = {};
    playerHintBalances = {};
    isPlayerHintBalancesLoaded = false;

    // Остальные поля общего профиля.
    dailyAttempts = 1;
    earnedCoins = 0;

    constructor() {
        makeAutoObservable(this);
    }

    setIsActive(value) {
        this.isActive = value;
    }

    setGameID(value) {
        this.gameID = value;
    }

    setScore(value) {
        this.score = Number(value) || 0;
    }

    setBestScore(value) {
        this.bestScore = Number(value) || 0;
    }

    setComboCount(value) {
        this.comboCount = Number(value) || 0;
    }

    // Обновляет отметку времени для окна комбо.
    setLastComboTimestamp(value) {
        this.lastComboTimestamp = value;
    }

    // Сбрасывает серию комбо.
    resetCombo() {
        this.comboCount = 0;
        this.lastComboTimestamp = null;
    }

    setTimeLeft(value) {
        this.timeLeft = Number(value) || 0;
    }

    setTimeLimitSeconds(value) {
        this.timeLimitSeconds = Number(value) || DEFAULT_GAME_TIME_SECONDS;
    }

    setGridSize(value) {
        const size = Number(value);
        this.gridSize = Number.isFinite(size) && size > 0 ? size : GRID_SIZE;
    }

    setGrid(value) {
        this.grid = normalizeGrid(value);
    }

    setSelectionCells(cells) {
        if (!Array.isArray(cells)) {
            this.selectionCells = [];
            return;
        }

        this.selectionCells = cells
            .map((item) => ({
                row: Number(item?.row),
                col: Number(item?.col),
            }))
            .filter((item) => Number.isFinite(item.row) && Number.isFinite(item.col));
    }

    clearSelection() {
        this.selectionCells = [];
    }

    setTargetWordLengths(lengths) {
        if (!Array.isArray(lengths)) {
            this.targetWordLengths = [];
            return;
        }

        this.targetWordLengths = lengths
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
            .sort((a, b) => a - b);
    }

    setFoundWords(words) {
        this.foundWords = Array.isArray(words) ? words.map((value) => String(value).toLowerCase()) : [];
    }

    setTargetWordPaths(paths) {
        this.targetWordPaths = Array.isArray(paths) ? paths : [];
    }

    addFoundWord(word) {
        const normalized = String(word ?? "").toLowerCase();
        if (!normalized || this.foundWords.includes(normalized)) {
            return;
        }
        this.foundWords = [...this.foundWords, normalized];
    }

    setFoundWordPaths(paths) {
        this.foundWordPaths = Array.isArray(paths) ? paths : [];
    }

    setHintWallet(wallet) {
        this.hintWallet = wallet || {};
    }

    setHintLimits(limits) {
        this.hintLimits = limits || {};
    }

    setPlayerHintBalances(value) {
        this.playerHintBalances = value || {};
        this.isPlayerHintBalancesLoaded = true;
    }

    setDailyAttempts(value) {
        this.dailyAttempts = Number(value) || 0;
    }

    setEarnedCoins(value) {
        this.earnedCoins = Number(value) || 0;
    }

    // Полный сброс состояния текущей сессии word-search.
    flush() {
        this.isActive = true;
        this.gameID = "";
        this.score = 0;
        this.comboCount = 0;
        this.lastComboTimestamp = null;
        this.timeLeft = DEFAULT_GAME_TIME_SECONDS;
        this.timeLimitSeconds = DEFAULT_GAME_TIME_SECONDS;
        this.gridSize = GRID_SIZE;
        this.grid = buildEmptyGrid();
        this.selectionCells = [];
        this.targetWordLengths = [];
        this.targetWordPaths = [];
        this.foundWords = [];
        this.foundWordPaths = [];
        this.hintWallet = {};
        this.hintLimits = {};
        this.playerHintBalances = {};
        this.isPlayerHintBalancesLoaded = false;
        this.earnedCoins = 0;
    }
}

const GameStore = new Game();
export default GameStore;
